/**
 * Máy chủ giả của gói tài nguyên (ADR-0091 mục 1, tenant-service): `/api/v1/security/resource-packages`.
 *
 * Bốn gói mặc định ứng với bốn `plan_code` (số của V22: 1, 10, 50, 200 triệu token mỗi tháng; người dùng, dung lượng
 * của V3 và tin thông báo của V35 — ENTERPRISE chưa đặt người dùng, dung lượng) cộng hai gói tự tạo:
 * "Chuyên nghiệp mở rộng" (gói nền PROFESSIONAL, 80 triệu, Phương Nam đang dùng) và "Dùng thử kéo dài" (đã ngừng,
 * không tenant nào dùng). Luật như backend: gói mặc định không ngừng được, không đổi gói nền; chỉ ngừng được gói
 * không tenant nào dùng; mã sinh từ tên khi bỏ trống, trùng thì thêm `_2`, `_3`.
 *
 * `tenant_count` đếm ở repository (cần danh sách tenant của `mock/tenant.ts`), không ở đây, để hai máy chủ giả không
 * import vòng nhau. Màu và nhãn không ở đây (`modules/goi-tai-nguyen/constants.ts`).
 */
import type { ApiError } from '../lib/errors';
import { sinhMa } from '../lib/ma-tu-sinh';
import type {
  PlanCode,
  ResourcePackage,
  ResourcePackageCreate,
  ResourcePackageRef,
  ResourcePackageUpdate,
  TenantQuota,
} from '../modules/tenant/types';

export type GoiTaiNguyenLuu = Omit<ResourcePackage, 'tenant_count'>;

const CAC_GOI_NEN: PlanCode[] = ['TRIAL', 'STANDARD', 'PROFESSIONAL', 'ENTERPRISE'];
const LUC = '2026-10-26T08:00:00+07:00';
const GiB = 1024 ** 3;

const macDinh = (
  plan_code: PlanCode,
  name: string,
  ai_tokens_month: number,
  max_users: number | null,
  db_bytes: number | null,
  notify_messages_month: number,
): GoiTaiNguyenLuu => ({
  code: plan_code,
  name,
  plan_code,
  ai_tokens_month,
  max_users,
  db_bytes,
  notify_messages_month,
  is_active: true,
  is_default: true,
  created_at: LUC,
  updated_at: LUC,
});

let cacGoi: GoiTaiNguyenLuu[] = [
  macDinh('TRIAL', 'Dùng thử', 1_000_000, 10, 1 * GiB, 1_000),
  macDinh('STANDARD', 'Tiêu chuẩn', 10_000_000, 100, 20 * GiB, 10_000),
  macDinh('PROFESSIONAL', 'Chuyên nghiệp', 50_000_000, 500, 100 * GiB, 50_000),
  macDinh('ENTERPRISE', 'Doanh nghiệp', 200_000_000, null, null, 200_000),
  {
    code: 'CHUYEN_NGHIEP_MO_RONG',
    name: 'Chuyên nghiệp mở rộng',
    plan_code: 'PROFESSIONAL',
    ai_tokens_month: 80_000_000,
    max_users: 800,
    db_bytes: 250 * GiB,
    notify_messages_month: 80_000,
    is_active: true,
    is_default: false,
    created_at: '2026-10-20T09:30:00+07:00',
    updated_at: '2026-10-20T09:30:00+07:00',
  },
  {
    code: 'DUNG_THU_KEO_DAI',
    name: 'Dùng thử kéo dài',
    plan_code: 'TRIAL',
    ai_tokens_month: 3_000_000,
    max_users: 10,
    db_bytes: 2 * GiB,
    notify_messages_month: 1_000,
    is_active: false,
    is_default: false,
    created_at: '2026-09-02T14:00:00+07:00',
    updated_at: '2026-10-01T10:15:00+07:00',
  },
];

function loiTruong(pointer: string, detail: string): ApiError {
  return {
    status: 400,
    code: 'INVALID_ARGUMENT',
    message: 'Thông tin gói chưa đúng, vui lòng sửa trường được đánh dấu.',
    details: [{ pointer, detail }],
  };
}

function kiemTen(name: unknown): string {
  const ten = typeof name === 'string' ? name.trim() : '';
  if (!ten) throw loiTruong('#/name', 'Nhập tên gói.');
  if (ten.length > 120) throw loiTruong('#/name', 'Tên gói tối đa 120 ký tự.');
  return ten;
}

function kiemToken(v: unknown): number {
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > 1_000_000_000_000) {
    throw loiTruong('#/ai_tokens_month', 'Token mỗi tháng là số nguyên từ 0 đến 1.000.000.000.000.');
  }
  return v;
}

/** Ba hạn mức của ADR-0105: tên trường, trần, câu lỗi như tenant-service. */
const HAN_MUC = [
  ['max_users', 1_000_000, 'Số người dùng tối đa phải từ 1 tới 1.000.000.'],
  ['db_bytes', 1024 ** 5, 'Dung lượng database phải lớn hơn 0 và không quá 1.048.576 GB.'],
  ['notify_messages_month', 1_000_000_000, 'Số tin thông báo mỗi tháng phải từ 1 tới 1.000.000.000.'],
] as const;

function kiemHanMuc(du: Partial<Record<(typeof HAN_MUC)[number][0], unknown>>, batBuoc: boolean): void {
  for (const [truong, toiDa, cau] of HAN_MUC) {
    const v = du[truong];
    if (v === undefined || v === null) {
      if (batBuoc) throw loiTruong(`#/${truong}`, 'Nhập đủ ba hạn mức của gói.');
      continue;
    }
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > toiDa) throw loiTruong(`#/${truong}`, cau);
  }
}

const sapXep = (a: GoiTaiNguyenLuu, b: GoiTaiNguyenLuu) =>
  Number(b.is_default) - Number(a.is_default) ||
  (a.is_default ? CAC_GOI_NEN.indexOf(a.plan_code) - CAC_GOI_NEN.indexOf(b.plan_code) : a.name.localeCompare(b.name, 'vi'));

const ref = (g: GoiTaiNguyenLuu): ResourcePackageRef => ({
  code: g.code,
  name: g.name,
  plan_code: g.plan_code,
  ai_tokens_month: g.ai_tokens_month,
  is_default: g.is_default,
});

export const mayChuGoiTaiNguyen = {
  /** Gói mặc định trước theo thứ tự `plan_code`, rồi gói tự tạo theo tên. */
  danhSach: (): GoiTaiNguyenLuu[] => [...cacGoi].sort(sapXep).map((g) => ({ ...g })),

  /** Gói đang áp cho một tenant: gói đã gán, không thì gói mặc định của `plan_code`. */
  cuaTenant: (ma: string | null, plan: PlanCode): ResourcePackageRef => {
    const g = (ma && cacGoi.find((x) => x.code === ma)) || cacGoi.find((x) => x.is_default && x.plan_code === plan);
    return ref(g as GoiTaiNguyenLuu);
  },

  /**
   * Dòng hạn mức của gói đang áp cho một tenant (ADR-0105): người dùng, dung lượng, tin thông báo đã đặt số. Số chưa
   * đặt (ENTERPRISE theo hợp đồng) thì không có dòng, như `plan_quota` không có dòng.
   */
  dongHanMuc: (ma: string | null, plan: PlanCode): TenantQuota[] => {
    const g = (ma && cacGoi.find((x) => x.code === ma)) || cacGoi.find((x) => x.is_default && x.plan_code === plan);
    if (!g) return [];
    const dong: [string, number | null][] = [
      ['MAX_USERS', g.max_users],
      ['DB_BYTES', g.db_bytes],
      ['NOTIFY_MESSAGES_MONTH', g.notify_messages_month],
    ];
    return dong
      .filter((d): d is [string, number] => d[1] !== null)
      .map(([resource, limit_value]) => ({ resource, limit_value, used: null }));
  },

  /** Gói (đang bật) theo mã; `undefined` khi không có hoặc đã ngừng. */
  timDangBat: (ma: string): GoiTaiNguyenLuu | undefined => cacGoi.find((x) => x.code === ma && x.is_active),

  /** Token mỗi tháng của gói mặc định một `plan_code` (`plan_limit`). */
  tokenMacDinh: (plan: PlanCode): number =>
    (cacGoi.find((x) => x.is_default && x.plan_code === plan) as GoiTaiNguyenLuu).ai_tokens_month,

  /** `PUT /api/v1/security/ai-token-quota/{plan_code}` vẫn sửa số của gói mặc định. */
  datTokenMacDinh: (plan: PlanCode, so: number): void => {
    cacGoi = cacGoi.map((x) => (x.is_default && x.plan_code === plan ? { ...x, ai_tokens_month: so } : x));
  },

  tao: (du: ResourcePackageCreate): GoiTaiNguyenLuu => {
    const name = kiemTen(du.name);
    const ai_tokens_month = kiemToken(du.ai_tokens_month);
    kiemHanMuc(du, true);
    if (!CAC_GOI_NEN.includes(du.plan_code)) throw loiTruong('#/plan_code', 'Chọn gói nền.');
    let code = du.code?.trim() || sinhMa(name, { tienTo: 'GOI', toiDa: 40 });
    if (du.code && cacGoi.some((x) => x.code === code)) {
      throw loiTruong('#/code', `Mã ${code} đã có. Đổi tên gói để sinh mã khác.`);
    }
    for (let i = 2; cacGoi.some((x) => x.code === code); i++) code = `${code.replace(/_\d+$/, '')}_${i}`;
    const luc = new Date().toISOString();
    const moi: GoiTaiNguyenLuu = {
      code,
      name,
      plan_code: du.plan_code,
      ai_tokens_month,
      max_users: du.max_users,
      db_bytes: du.db_bytes,
      notify_messages_month: du.notify_messages_month,
      is_active: true,
      is_default: false,
      created_at: luc,
      updated_at: luc,
    };
    cacGoi = [...cacGoi, moi];
    return { ...moi };
  },

  /**
   * `PATCH …/{code}`. `soTenant` do repository đếm: ngừng gói đang có tenant là 400. Đổi gói nền của gói tự tạo thì
   * repository kéo `plan_code` của tenant theo.
   */
  sua: (code: string, du: ResourcePackageUpdate, soTenant: number): GoiTaiNguyenLuu => {
    const g = cacGoi.find((x) => x.code === code);
    if (!g) throw { status: 404, code: 'NOT_FOUND', message: `Không có gói tài nguyên ${code}.` } satisfies ApiError;
    const moi = { ...g };
    if (du.name !== undefined && g.is_default && du.name !== g.name) {
      throw loiTruong('#/name', 'Gói hệ thống không đổi được tên; chỉ sửa được các hạn mức.');
    }
    if (du.name !== undefined) moi.name = kiemTen(du.name);
    if (du.ai_tokens_month !== undefined) moi.ai_tokens_month = kiemToken(du.ai_tokens_month);
    kiemHanMuc(du, false);
    if (du.max_users !== undefined) moi.max_users = du.max_users;
    if (du.db_bytes !== undefined) moi.db_bytes = du.db_bytes;
    if (du.notify_messages_month !== undefined) moi.notify_messages_month = du.notify_messages_month;
    if (du.plan_code !== undefined && du.plan_code !== g.plan_code) {
      if (g.is_default) throw loiTruong('#/plan_code', 'Gói hệ thống không đổi được gói nền; chỉ sửa được các hạn mức.');
      moi.plan_code = du.plan_code;
    }
    if (du.is_active !== undefined && du.is_active !== g.is_active) {
      if (!du.is_active && g.is_default) throw loiTruong('#/is_active', 'Gói hệ thống không ngừng được.');
      if (!du.is_active && soTenant > 0) {
        throw loiTruong(
          '#/is_active',
          `Còn ${soTenant} tenant đang dùng gói này. Chuyển các tenant đó sang gói khác rồi ngừng gói.`,
        );
      }
      moi.is_active = du.is_active;
    }
    moi.updated_at = new Date().toISOString();
    cacGoi = cacGoi.map((x) => (x.code === code ? moi : x));
    return { ...moi };
  },
};
